import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiSearch, BiSend } from "react-icons/bi";
import { BsPencil, BsPlusLg, BsTrash } from "react-icons/bs";
import { HiOutlineMail } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { usePagination, useTable } from "react-table";
import Loader from "../../components/Loader";
import Select from "../../components/Select";
import Api from "../../service/http";

const Contacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  console.log("Contacts component rendered");
  console.log(contacts);
  const [categories, setCategories] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageCategory, setMessageCategory] = useState('');
  const [messageText, setMessageText] = useState('');

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchCategories = async () => {
    try {
      const response = await Api.get('web/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchContacts = async (searchTerm = '', categoryId = '', page = 1) => {
    setLoading(true);
    try {
      const response = await Api.get(`web/contacts?page=${page}&search=${searchTerm}&category_id=${categoryId}`);
      const data = response.data;
      setContacts(data.data);
      setPageCount(data.last_page);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce((value, category, page) => {
    fetchContacts(value, category, page);
  }, 500), []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    debouncedFetch(search, selectedCategory, pagination.pageIndex + 1);
  }, [search, selectedCategory, pagination.pageIndex]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleDelete = async () => {
    try {
      await Api.delete(`web/contacts/${deleteId}`);
      alert("Contact deleted successfully!");
      setDeleteId(null);
      setShowConfirm(false);
      fetchContacts(search, selectedCategory, pagination.pageIndex + 1);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageCategory) {
      alert("Please select a category");
      return;
    }
    if (!messageText.trim()) {
      alert("Please enter a message");
      return;
    }

    try {
      await Api.post('web/send-email', {
        category_id: messageCategory,
        message: messageText
      });
      alert("Message sent successfully!");
      setShowMessagePopup(false);
      setMessageCategory('');
      setMessageText('');
    } catch (error) {
      console.error("Send message failed:", error);
      alert("Failed to send message. Please try again.");
    }
  };

  const columns = useMemo(() => [
    { Header: "First Name", accessor: "first_name" },
    { Header: "Last Name", accessor: "last_name" },
    { Header: "Email", accessor: "email" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Note", accessor: "note" },
    // { Header: "Virtual Number", accessor: "emailable_number" },
    {
      Header: "Categories",
      accessor: "categories",
      Cell: ({ value }) => (
        <div className="flex flex-wrap gap-1">
          {value && value?.map(category => (
            <span key={category.id} className="bg-indigo-50 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
              {category.name}
            </span>
          ))}
        </div>
      )
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => navigate(`/inbox/${row.original.id}`)}
            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Send Message"
          >
            <BiSend className="text-lg" />
          </button>
          <button 
            onClick={() => navigate(`/contacts/edit/${row.original.id}`)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit Contact"
          >
            <BsPencil className="text-lg" />
          </button>
          <button 
            onClick={() => { setDeleteId(row.original.id); setShowConfirm(true); }}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Contact"
          >
            <BsTrash className="text-lg" />
          </button>
        </div>
      )
    },
  ], [navigate, setSelectedContact, setShowMessagePopup, setMessageCategory, setMessageText]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: tableState,
  } = useTable(
    { columns, data: contacts, manualPagination: true, pageCount, initialState: { pageIndex: pagination.pageIndex } },
    usePagination
  );

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: tableState.pageIndex }));
  }, [tableState.pageIndex]);

  return (
    <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
            <p className="text-gray-500 text-sm mt-1">Manage and organize your contact list efficiently.</p>
          </div>
          <div className="flex gap-3">
            <button
              className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2.5 rounded-xl border border-gray-200 font-semibold text-sm hover:bg-gray-50 transition-all shadow-sm"
              onClick={() => setShowMessagePopup(true)}
            >
              <HiOutlineMail className="text-lg" />
              Send Message
            </button>
            <button
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100"
              onClick={() => navigate('/contacts/add')}
            >
              <BsPlusLg />
              Add Contact
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/30">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                <input
                  type="text"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search contacts by name, email or phone..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                />
              </div>
              <div className="w-full md:w-64">
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={categories?.map(cat => ({ label: cat.name, value: cat.id }))}
                  placeholder="All Categories"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full">
              <thead>
                {headerGroups?.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-50/50">
                    {headerGroup.headers?.map(column => (
                      <th {...column.getHeaderProps()} className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-20">
                      <Loader className="mx-auto" />
                    </td>
                  </tr>
                ) : page?.length > 0 ? (
                  page?.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50/50 transition-colors">
                        {row.cells?.map(cell => (
                          <td {...cell.getCellProps()} className="px-6 py-4 text-sm text-gray-600">
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                          <BiSearch className="text-2xl text-gray-300" />
                        </div>
                        <p>No contacts found matching your criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
            <div className="text-sm text-gray-500 font-medium">
              Showing page <span className="text-gray-900">{pagination.pageIndex + 1}</span> of <span className="text-gray-900">{pageCount}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Previous
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden transform transition-all">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BsTrash className="text-2xl text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Contact</h3>
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete this contact? This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all text-sm shadow-md shadow-red-100"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Popup */}
      {showMessagePopup && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden transform transition-all">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Send Message</h3>
              <button 
                onClick={() => setShowMessagePopup(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-5">
                <Select
                  label="Category"
                  value={messageCategory}
                  onChange={(e) => setMessageCategory(e.target.value)}
                  options={categories?.map(cat => ({ label: cat.name, value: cat.id }))}
                  placeholder="Select Category"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm h-40 resize-none"
                  placeholder="Type your message here..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3">
                <button 
                  onClick={() => setShowMessagePopup(false)} 
                  className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSendMessage} 
                  className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all text-sm shadow-md shadow-indigo-100 flex items-center gap-2"
                >
                  <BiSend className="text-lg" />
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
